// ============================================================================
// PROMPT 24 — High-Precision Localization & Financial Convention Engines
// ============================================================================

import { LOCALES_REGISTRY, FINANCIAL_CONVENTIONS, SAMPLE_TRANSLATION_UNITS } from './globalizationRegistry';
import { LocaleDefinition, TextDirection, HreflangRelation, MarketLaunchCandidate } from './globalizationTypes';

/**
 * 1. Authoritative Number Formatter
 * Uses standard Intl.NumberFormat backed by BCP 47 locale conventions without manual string hackery.
 */
export class YamooPayNumberFormatter {
  public static formatDecimal(value: number, locale = 'en-US', minDecimals = 2, maxDecimals = 2): string {
    if (isNaN(value)) return '0';
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals
      }).format(value);
    } catch {
      return value.toFixed(maxDecimals);
    }
  }

  public static formatPercent(value: number, locale = 'en-US', decimals = 2): string {
    if (isNaN(value)) return '0%';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value / 100);
    } catch {
      return `${value.toFixed(decimals)}%`;
    }
  }

  public static formatInteger(value: number, locale = 'en-US'): string {
    if (isNaN(value)) return '0';
    try {
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
    } catch {
      return Math.round(value).toString();
    }
  }
}

/**
 * 2. Currency Presentation & Disambiguation Engine
 * Supports ISO 4217, narrow symbols vs disambiguated symbols (US$ vs CA$ vs AU$), and minor units (JPY=0, BHD=3).
 */
export class YamooPayCurrencyFormatter {
  private static ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'XAF', 'XOF', 'CLP', 'VND', 'UGX', 'RWF', 'GNF']);
  private static THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'KWD', 'OMR', 'JOD', 'TND']);

  public static getMinorUnitDecimals(currencyCode: string): number {
    const code = currencyCode.toUpperCase();
    if (this.ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
    if (this.THREE_DECIMAL_CURRENCIES.has(code)) return 3;
    return 2;
  }

  public static formatCurrency(
    amount: number,
    currencyCode = 'USD',
    locale = 'en-US',
    options?: { disambiguateSymbol?: boolean; showCode?: boolean }
  ): string {
    const code = currencyCode.toUpperCase();
    const decimals = this.getMinorUnitDecimals(code);

    try {
      let formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);

      // Symbol disambiguation for ambiguous currencies like "$"
      if (options?.disambiguateSymbol) {
        if (code === 'CAD' && formatted.includes('$') && !formatted.includes('CA$') && !formatted.includes('CAD')) {
          formatted = formatted.replace('$', 'CA$');
        } else if (code === 'AUD' && formatted.includes('$') && !formatted.includes('AU$') && !formatted.includes('AUD')) {
          formatted = formatted.replace('$', 'AU$');
        } else if (code === 'USD' && locale !== 'en-US' && formatted.includes('$') && !formatted.includes('US$')) {
          formatted = formatted.replace('$', 'US$');
        }
      }

      if (options?.showCode && !formatted.includes(code)) {
        formatted = `${formatted} ${code}`;
      }

      return formatted;
    } catch {
      return `${code} ${amount.toFixed(decimals)}`;
    }
  }
}

/**
 * 3. Date & Time Convention Engine
 * Distinguishes date-only values (birth dates, tax year bounds) from UTC timestamps to avoid timezone shifts.
 */
export class YamooPayDateFormatter {
  public static formatDateOnly(isoDate: string, locale = 'en-US'): string {
    if (!isoDate) return '';
    try {
      const [year, month, day] = isoDate.split('-').map(Number);
      if (!year || !month || !day) return isoDate;
      const date = new Date(year, month - 1, day); // local date only
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
    } catch {
      return isoDate;
    }
  }

  public static formatTimestamp(utcTimestamp: string | number | Date, locale = 'en-US'): string {
    try {
      const date = new Date(utcTimestamp);
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(date);
    } catch {
      return String(utcTimestamp);
    }
  }
}

/**
 * 4. Localized Financial Input Parser
 * Parses diverse international input strings:
 * - "1,000.50" (US/UK)
 * - "1 000,50" (FR/CM)
 * - "1.000,50" (DE/ES)
 * - "1'000.50" (CH)
 * - "١٬٠٠٠٫٥٠" (AR)
 * Converts non-Latin digits and yields exact lossless numeric values.
 */
export class LocalizedFinancialInputParser {
  private static ARABIC_DIGIT_MAP: Record<string, string> = {
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
    '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
    '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
    '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
    '٫': '.', '٬': ''
  };

  public static normalizeDigits(raw: string): string {
    let result = '';
    for (const char of raw) {
      result += this.ARABIC_DIGIT_MAP[char] !== undefined ? this.ARABIC_DIGIT_MAP[char] : char;
    }
    return result;
  }

  public static parse(input: string, locale = 'en-US'): { value: number; isValid: boolean; normalizedString: string } {
    if (!input || typeof input !== 'string') {
      return { value: 0, isValid: false, normalizedString: '0' };
    }

    // Step 1: Normalize Arabic/Indic digits and punctuation
    let cleaned = this.normalizeDigits(input.trim());

    // Step 2: Remove currency symbols and extraneous whitespace
    cleaned = cleaned.replace(/[$€£¥₹\s\u00A0\u202F]/g, '');

    // Handle Swiss apostrophe group separators e.g. 1'000.50
    cleaned = cleaned.replace(/'/g, '');

    // Step 3: Check locale definition for decimal separator hint
    const localeDef = LOCALES_REGISTRY.find((l) => l.bcp47.toLowerCase() === locale.toLowerCase());
    const expectedDecimal = localeDef?.decimalSeparator || '.';

    // Step 4: Disambiguate decimal vs grouping separators
    let canonical = cleaned;

    if (expectedDecimal === ',') {
      // European format: 1.000,50 or 1000,50
      if (cleaned.includes(',') && cleaned.includes('.')) {
        // Has both dots (grouping) and commas (decimal)
        canonical = cleaned.replace(/\./g, '').replace(',', '.');
      } else if (cleaned.includes(',')) {
        canonical = cleaned.replace(',', '.');
      } else if (cleaned.includes('.')) {
        // Only dots. Check if it looks like thousands grouping e.g. 1.000 or a decimal
        const parts = cleaned.split('.');
        if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3 && parts[0].length <= 3)) {
          // Likely grouping separator
          canonical = cleaned.replace(/\./g, '');
        }
      }
    } else {
      // Standard dot decimal: 1,000.50 or 1000.50
      if (cleaned.includes(',') && cleaned.includes('.')) {
        canonical = cleaned.replace(/,/g, '');
      } else if (cleaned.includes(',')) {
        // Check if comma was used as decimal by mistake or grouping
        const parts = cleaned.split(',');
        if (parts.length === 2 && parts[1].length !== 3) {
          // Single comma followed by 1, 2, or 4+ digits -> user likely typed comma for decimal
          canonical = cleaned.replace(',', '.');
        } else {
          canonical = cleaned.replace(/,/g, '');
        }
      }
    }

    const numericValue = parseFloat(canonical);
    const isValid = !isNaN(numericValue) && isFinite(numericValue);

    return {
      value: isValid ? numericValue : 0,
      isValid,
      normalizedString: isValid ? canonical : '0'
    };
  }
}

/**
 * 5. Pseudolocalization Engine for QA & Layout Stress Testing
 * Replaces ASCII letters with accented glyphs, expands text by ~35%, and wraps in brackets.
 */
export class PseudolocalizationEngine {
  private static ACCENT_MAP: Record<string, string> = {
    a: 'à', b: 'ƀ', c: 'ċ', d: 'ḓ', e: 'è', f: 'ƒ', g: 'ġ', h: 'ḧ',
    i: 'ì', j: 'ĵ', k: 'ḱ', l: 'ĺ', m: 'ḿ', n: 'ñ', o: 'ö', p: 'ṗ',
    q: 'ʠ', r: 'ŕ', s: 'š', t: 'ẗ', u: 'ù', v: 'ṽ', w: 'ẅ', x: 'ẋ',
    y: 'ý', z: 'ž',
    A: 'À', B: 'Ɓ', C: 'Ċ', D: 'Ḓ', E: 'È', F: 'Ƒ', G: 'Ġ', H: 'Ḧ',
    I: 'Ì', J: 'Ĵ', K: 'Ḱ', L: 'Ĺ', M: 'Ḿ', N: 'Ñ', O: 'Ö', P: 'Ṗ',
    Q: 'Ǫ', R: 'Ŕ', S: 'Š', T: 'Ṫ', U: 'Ù', V: 'Ṽ', W: 'Ẅ', X: 'Ẋ',
    Y: 'Ý', Z: 'Ž'
  };

  public static transform(text: string, expandPercentage = 35): string {
    if (!text) return '';

    let transformed = '';
    let inVariable = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      // Preserve variables like {name} or {amount}
      if (char === '{') {
        inVariable = true;
        transformed += char;
        continue;
      }
      if (char === '}') {
        inVariable = false;
        transformed += char;
        continue;
      }

      if (inVariable) {
        transformed += char;
      } else {
        transformed += this.ACCENT_MAP[char] || char;
      }
    }

    // Add expansion padding to stress layout
    const extraChars = Math.round((text.length * expandPercentage) / 100);
    const expansion = ' ṫèšẗ'.repeat(Math.max(1, Math.ceil(extraChars / 5))).substring(0, extraChars);

    return `[ !!! ${transformed}${expansion ? ' ' + expansion : ''} !!! ]`;
  }
}

/**
 * 6. Bidirectional UI & RTL Engine
 * Logical CSS properties, directional icon mirroring vs non-mirroring, and bidi isolation.
 */
export class BidirectionalUIEngine {
  public static isRTL(localeOrLang: string): boolean {
    const code = localeOrLang.toLowerCase();
    return code.startsWith('ar') || code.startsWith('he') || code.startsWith('fa') || code.startsWith('ur');
  }

  public static getTextDirection(localeOrLang: string): TextDirection {
    return this.isRTL(localeOrLang) ? 'RTL' : 'LTR';
  }

  /**
   * Only mirror directional action indicators (arrows, chevrons, step markers).
   * DO NOT mirror: currency symbols, brand logos, play/pause controls, or charts.
   */
  public static shouldMirrorIcon(iconType: 'CHEVRON_LEFT' | 'CHEVRON_RIGHT' | 'ARROW_LEFT' | 'ARROW_RIGHT' | 'LOGO' | 'CURRENCY' | 'CHART' | 'PLAY'): boolean {
    switch (iconType) {
      case 'CHEVRON_LEFT':
      case 'CHEVRON_RIGHT':
      case 'ARROW_LEFT':
      case 'ARROW_RIGHT':
        return true;
      default:
        return false;
    }
  }

  /**
   * Wraps mixed-direction content (e.g. "SAR 500.00" in Arabic text) with Unicode First Strong Isolate markers.
   */
  public static isolateBidi(text: string): string {
    return `\u2068${text}\u2069`;
  }
}

/**
 * 7. Locale Fallback Engine & STRICT JURISDICTION SEPARATION
 * Language fallbacks: fr-CA -> fr -> en (for generic UI strings).
 * CRITICAL RULE: Language Fallback NEVER cross-contaminates Tax/Financial Jurisdictions!
 */
export class LocaleFallbackEngine {
  public static getFallbackLanguages(locale: string): string[] {
    const parts = locale.split('-');
    const baseLang = parts[0].toLowerCase();

    if (parts.length > 1) {
      return [locale, baseLang, 'en'];
    }
    return [locale, 'en'];
  }

  public static resolveMessage(messageKey: string, requestedLocale: string): string {
    const chain = this.getFallbackLanguages(requestedLocale);

    for (const loc of chain) {
      const unit = SAMPLE_TRANSLATION_UNITS.find(
        (u) => u.messageKey === messageKey && u.locale.toLowerCase().startsWith(loc.toLowerCase()) && u.status === 'PUBLISHED'
      );
      if (unit) return unit.translatedText;
    }

    return messageKey;
  }

  /**
   * CRITICAL: Prohibits fallback across financial jurisdictions.
   * If Canadian French salary rule is missing in French, it shows verified Canadian rule in English,
   * NEVER France DGFiP rule!
   */
  public static validateJurisdictionIntegrity(userJurisdiction: string, targetJurisdiction: string): { isSafe: boolean; warning?: string } {
    if (userJurisdiction !== targetJurisdiction) {
      return {
        isSafe: false,
        warning: `CRITICAL INTEGRITY ENFORCED: Requested statutory rules for jurisdiction ${targetJurisdiction} do not match active tax residence ${userJurisdiction}. Tax rules must never fallback across national jurisdictions.`
      };
    }
    return { isSafe: true };
  }
}

/**
 * 8. Hreflang Graph & International SEO Service
 */
export class HreflangGraphService {
  public static generateHreflangTags(canonicalPath: string, supportedLocales: string[]): HreflangRelation[] {
    const baseDomain = 'https://yamoopay.com';
    const cleanPath = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;

    const relations: HreflangRelation[] = supportedLocales.map((loc) => ({
      url: `${baseDomain}/${loc.toLowerCase()}${cleanPath}`,
      locale: loc.toLowerCase(),
      canonicalUrl: `${baseDomain}/${loc.toLowerCase()}${cleanPath}`,
      isReciprocal: true,
      httpStatus: 200
    }));

    // Add x-default pointing to global English root
    relations.push({
      url: `${baseDomain}${cleanPath}`,
      locale: 'x-default',
      canonicalUrl: `${baseDomain}${cleanPath}`,
      isReciprocal: true,
      httpStatus: 200
    });

    return relations;
  }

  public static validateReciprocalLinks(relations: HreflangRelation[]): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    const hasXDefault = relations.some((r) => r.locale === 'x-default');

    if (!hasXDefault) {
      issues.push('Missing required x-default hreflang tag for global search selector');
    }

    const non200s = relations.filter((r) => r.httpStatus !== 200);
    if (non200s.length > 0) {
      issues.push(`Detected ${non200s.length} hreflang alternates returning non-200 HTTP status`);
    }

    return {
      isValid: issues.length === 0,
      issues
    };
  }
}

/**
 * 9. Market Launch Factory & Gate Service
 */
export class MarketLaunchFactoryService {
  public static evaluateGates(candidate: MarketLaunchCandidate): {
    canLaunchFree: boolean;
    canLaunchPaid: boolean;
    missingFreeGates: string[];
    missingPaidGates: string[];
  } {
    const g = candidate.gatesPassed;
    const missingFreeGates: string[] = [];
    const missingPaidGates: string[] = [];

    if (!g.countryPackValid) missingFreeGates.push('Country Pack Validation');
    if (!g.localeUxPassed) missingFreeGates.push('Locale & RTL UX Validation');
    if (!g.financialDataPassed) missingFreeGates.push('Financial Rule Independence Validation');
    if (!g.compliancePassed) missingFreeGates.push('Compliance & Privacy Certification');
    if (!g.accessibilityPassed) missingFreeGates.push('WCAG 2.2 AA Accessibility');

    if (missingFreeGates.length > 0) {
      missingPaidGates.push(...missingFreeGates);
    }
    if (!g.qualityCertified) missingPaidGates.push('P23 Release Certification');
    if (!g.paymentReadyIfPaid) missingPaidGates.push('P21 Payment Gateway & Tax Verification');

    return {
      canLaunchFree: missingFreeGates.length === 0,
      canLaunchPaid: missingPaidGates.length === 0,
      missingFreeGates,
      missingPaidGates
    };
  }
}
